import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-560c047a"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "hardware-modify-log-drawer-body" }

import { reactive, ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import type { HardwareStatCardKey } from './_components/hardware-statistics-cards.types'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  HardwareService,
  HardwareItem,
  HardwareListSliceFilters
} from './hardware.service'
import Card from '@/views/dashboard/_components/card/card.vue'
import StatisticsCards from './_components/statistics-cards.vue'
import CpuBrandDistributionChart, { CpuBrandSliceClickPayload } from './_components/cpu-brand-distribution-chart.vue'
import DiskCapacityDistributionChart from './_components/disk-capacity-distribution-chart.vue'
import MemorySpecStatisticsChart from './_components/memory-spec-statistics-chart.vue'
import MotherboardTop5Chart from './_components/motherboard-top5-chart.vue'
import HardwareTerminalList from './_components/hardware-terminal-list.vue'
import HardwareChangeRecordList from './_components/hardware-change-record-list.vue'
import HardwareModifyLog from '@/views/dashboard/notice-manage/hardware-modify-log/hardware-modify-log.vue'
import { TableViewMode } from '@/views/dashboard/_enum/table-view-mode'
import {
  resolveDiskSliceNameToDropdownLabel,
  resolveMemorySliceNameToDropdownLabel,
} from './_utils/hardware-fixed-capacity-buckets'

type PieDatum = { name: string; value: number }

export default /*#__PURE__*/_defineComponent({
  __name: 'hardware',
  setup(__props) {

const injector = useReflectiveInjector([HardwareService])
const hardwareService = injector.get(HardwareService)
const { t } = useI18n()

const hardwareModifyLogDrawerVisible = ref(false)
const hardwareModifyLogEmbedDefaults = ref<{ dateRange: [string, string] } | null>(null)
const hardwareModifyLogDrawerTitle = ref('')

const terminalDrawerVisible = ref(false)
/** 抽屉内列表初始筛选；与 key 联动保证每次打开按最新条件挂载 */
const drawerListFilters = ref<HardwareListSliceFilters>({})
const drawerListKey = ref(0)

function openDrawerWithListFilters(filters: HardwareListSliceFilters) {
  drawerListFilters.value = {
    memory_spec: undefined,
    mainboard: undefined,
    cpu: undefined,
    disk_capacity: undefined,
    device_ids: undefined,
    ...filters,
  }
  drawerListKey.value += 1
  terminalDrawerVisible.value = true
}

function openTerminalDrawer() {
  openDrawerWithListFilters({})
}

/** 统计卡：终端数量 → 全量硬件列表抽屉；今日变更 → 硬件变更日志抽屉（当日范围） */
function handleHardwareStatClick (key: HardwareStatCardKey) {
  if (key === 'total') {
    openTerminalDrawer()
    return
  }
  if (key === 'todayChanged') {
    const today: [string, string] = [
      dayjs().format('YYYY-MM-DD'),
      dayjs().format('YYYY-MM-DD')
    ]
    hardwareModifyLogEmbedDefaults.value = { dateRange: today }
    hardwareModifyLogDrawerTitle.value = `${t('menu.hardwareModifyLog')} [${t('terminalHardware.todayChangedTerminals')}]`
    hardwareModifyLogDrawerVisible.value = true
  }
}

function onCpuBrandSliceClick(payload: { name: string; device_ids: string[] }) {
  const ids = (payload.device_ids || []).filter(Boolean)
  if (ids.length > 0) {
    openDrawerWithListFilters({ cpu: payload.name, device_ids: ids })
    return
  }
  openDrawerWithListFilters({ cpu: payload.name })
}

function onDiskCapacitySliceClick(name: string) {
  const trimmed = String(name).trim()
  if (!trimmed || trimmed === 'Unknown') return
  const bucket = resolveDiskSliceNameToDropdownLabel(trimmed) ?? trimmed
  openDrawerWithListFilters({ disk_capacity: bucket })
}

function onMemorySpecSliceClick(name: string) {
  const trimmed = String(name).trim()
  if (!trimmed || trimmed === 'Unknown') return
  const bucket = resolveMemorySliceNameToDropdownLabel(trimmed) ?? trimmed
  openDrawerWithListFilters({ memory_spec: bucket })
}

function onMainboardSliceClick(name: string) {
  openDrawerWithListFilters({ mainboard: name })
}

watch(terminalDrawerVisible, (visible) => {
  if (!visible) drawerListFilters.value = {}
})

const statistics = reactive({
  totalTerminals: 0,
  todayChangedTerminals: 0
})

const cpuBrandData = ref<Array<{ name: string; value: number; device_ids?: string[] }>>([])
const diskCapacityData = ref<Array<{ name: string; value: number }>>([])
const memorySpecPieData = ref<PieDatum[]>([])
const motherboardPieData = ref<PieDatum[]>([])

const dialogHistoryVisible = ref(false)
const historyDeviceId = ref('')
const dialogData = reactive({
  title: '',
})

function handlerViewHistory(row: HardwareItem) {
  historyDeviceId.value = row.device_id
  dialogData.title = row.computer_name || ''
  dialogHistoryVisible.value = true
}

async function loadStatistics() {
  try {
    const res = await hardwareService.getTerminalHardwareStatistics()
    statistics.totalTerminals = res.total_terminal || 0
    statistics.todayChangedTerminals = res.today_changed_terminal || 0
  } catch (error) {
    console.error('加载统计数据失败:', error)
  }
}

async function loadChartData() {
  try {
    const res = await hardwareService.getTerminalHardwareStatistics()

    cpuBrandData.value = (res.cpu_brand_distribution || []).map(item => ({
      name: item.brand_name || 'Unknown',
      value: item.count || 0,
      device_ids: item.device_ids || [],
    }))

    diskCapacityData.value = (res.disk_capacity_distribution || []).map(item => ({
      name: item.capacity || 'Unknown',
      value: item.count || 0
    }))

    const memoryEntries = (res.memory_statistics || []).map(item => ({
      name: item.capacity || 'Unknown',
      value: item.count || 0
    })).sort((a, b) => {
      const aMatch = a.name.match(/(\d+)/)
      const bMatch = b.name.match(/(\d+)/)
      const aNum = aMatch ? parseInt(aMatch[1]) : 0
      const bNum = bMatch ? parseInt(bMatch[1]) : 0
      return aNum - bNum
    })
    memorySpecPieData.value = memoryEntries

    motherboardPieData.value = (res.mainboard_top5 || []).map(item => ({
      name: item.model || 'Unknown',
      value: item.count || 0
    }))
  } catch (error) {
    console.error('加载图表数据失败:', error)
  }
}

onMounted(() => {
  loadStatistics()
  loadChartData()
})

return (_ctx: any,_cache: any) => {
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_drawer = _resolveComponent("el-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(StatisticsCards, {
      "total-terminals": statistics.totalTerminals,
      "today-changed-terminals": statistics.todayChangedTerminals,
      onStatClick: handleHardwareStatClick
    }, null, 8, ["total-terminals", "today-changed-terminals"]),
    _createVNode(_component_el_row, {
      gutter: 16,
      class: "cm-ui-mt16 cm-ui-mb16"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_col, { span: 6 }, {
          default: _withCtx(() => [
            _createVNode(CpuBrandDistributionChart, {
              data: cpuBrandData.value,
              onSliceClick: _cache[0] || (_cache[0] = (p) => onCpuBrandSliceClick(p))
            }, null, 8, ["data"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, { span: 6 }, {
          default: _withCtx(() => [
            _createVNode(DiskCapacityDistributionChart, {
              data: diskCapacityData.value,
              onSliceClick: onDiskCapacitySliceClick
            }, null, 8, ["data"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, { span: 6 }, {
          default: _withCtx(() => [
            _createVNode(MemorySpecStatisticsChart, {
              data: memorySpecPieData.value,
              onSliceClick: onMemorySpecSliceClick
            }, null, 8, ["data"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, { span: 6 }, {
          default: _withCtx(() => [
            _createVNode(MotherboardTop5Chart, {
              data: motherboardPieData.value,
              onSliceClick: onMainboardSliceClick
            }, null, 8, ["data"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(Card, {
      size: "second",
      class: "table-container",
      cardHeaderStyle: { marginBottom: '0' },
      title: _ctx.$t('terminalHardware.terminalList')
    }, {
      titleAfter: _withCtx(() => [
        _createVNode(_component_el_tag, { size: "small" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.listPreviewRecent10')), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(HardwareTerminalList, {
          "cpu-brand-options": cpuBrandData.value,
          "mainboard-options": motherboardPieData.value,
          onViewAll: openTerminalDrawer,
          onViewHistory: handlerViewHistory
        }, null, 8, ["cpu-brand-options", "mainboard-options"])
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(_component_el_drawer, {
      modelValue: terminalDrawerVisible.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((terminalDrawerVisible).value = $event)),
      size: "80%",
      "destroy-on-close": "",
      "append-to-body": "",
      title: _ctx.$t('terminalHardware.allTerminalHardwareList')
    }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(HardwareTerminalList, {
          key: drawerListKey.value,
          "view-mode": _unref(TableViewMode).full,
          "initial-hardware-filters": drawerListFilters.value,
          "cpu-brand-options": cpuBrandData.value,
          "mainboard-options": motherboardPieData.value
        }, null, 8, ["view-mode", "initial-hardware-filters", "cpu-brand-options", "mainboard-options"]))
      ]),
      _: 1
    }, 8, ["modelValue", "title"]),
    _createVNode(_component_el_drawer, {
      modelValue: hardwareModifyLogDrawerVisible.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((hardwareModifyLogDrawerVisible).value = $event)),
      size: "80%",
      "destroy-on-close": "",
      "append-to-body": "",
      class: "terminal-list-drawer hardware-modify-log-stat-drawer",
      title: hardwareModifyLogDrawerTitle.value
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          (hardwareModifyLogDrawerVisible.value && hardwareModifyLogEmbedDefaults.value)
            ? (_openBlock(), _createBlock(HardwareModifyLog, {
                key: 0,
                "embed-defaults": hardwareModifyLogEmbedDefaults.value
              }, null, 8, ["embed-defaults"]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "title"]),
    _createVNode(_component_el_drawer, {
      modelValue: dialogHistoryVisible.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((dialogHistoryVisible).value = $event)),
      "destroy-on-close": "",
      "append-to-body": "",
      direction: "rtl",
      size: "70%",
      title: `${dialogData.title}${_ctx.$t('terminalHardware.hardwareChangeRecordSuffix')}`
    }, {
      default: _withCtx(() => [
        (dialogHistoryVisible.value)
          ? (_openBlock(), _createBlock(HardwareChangeRecordList, {
              key: 0,
              "device-id": historyDeviceId.value
            }, null, 8, ["device-id"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ], 64))
}
}

})